/**
 * React Query hooks for Events
 * 
 * Standard pattern for event data fetching and mutations.
 * Reference implementation for Issue #178.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  eventsApi,
  type Event,
  type EventFilterParams,
  type CreateEventPayload,
  type CreateEventResponse,
  type UpdateEventPayload,
  type RSVPPayload,
  type UploadEventImageResponse,
  type PaceGroup,
  type PaceGroupResponse,
  type CreatePaceGroupPayload,
  type DeleteScope,
  type UpdatePaceGroupPayload,
} from '../lib/api/events';
import { useClubs } from './useClubs';
import {
  RequestDeduplicator,
  generateIdempotencyKey
} from '../lib/deduplication';
import { apiPost } from '../lib/api';
import { useOroMutation, OroOperation } from './useOroMutation';

/**
 * Fetch all events across all clubs the user is a member of
 * 
 * Query Key: ['events', 'my-clubs']
 * Returns: Array of events with RSVP info from user's clubs
 * 
 * Future: Will include nearby public races and events
 */
export function useAllEvents() {
  const { data: clubs } = useClubs();
  
  return useQuery({
    queryKey: ['events', 'my-clubs', clubs?.map(club => club.id).sort()],
    queryFn: async () => {
      if (!clubs || clubs.length === 0) {
        return [];
      }
      
      // Fetch events from all user's clubs in parallel
      const eventPromises = clubs.map(club => 
        eventsApi.getClubEvents(club.id)
      );
      
      const results = await Promise.all(eventPromises);
      
      // Flatten and sort by start date
      return results
        .flat()
        .sort((a, b) => a.starts_at - b.starts_at);
    },
    enabled: !!clubs && clubs.length > 0,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Fetch events for a specific club with optional date filtering
 * 
 * Query Key: ['events', { clubId, from?, to? }]
 * Returns: Array of events with RSVP info
 * 
 * Note: Backend supports from/to timestamp filtering via query params
 */
export function useClubEvents(clubId: string, filter?: EventFilterParams) {
  return useQuery({
    queryKey: ['events', { clubId, ...filter }],
    queryFn: () => eventsApi.getClubEvents(clubId, filter),
    enabled: !!clubId,
    staleTime: 1000 * 60 * 2, // 2 minutes - events don't change that frequently
  });
}

/**
 * Fetch single event by ID
 *
 * Query Key: ['event', eventId]
 * Returns: Event details with RSVP status and route data
 */
export function useEvent(
  eventId: string,
  options?: {
    staleTime?: number;
    // #2191: the duplicate-prefill consumer needs a FRESH source on mount
    // (the shared ['event', id] cache may hold a stale detail from a recent
    // view) but must NOT refetch on window focus (that would re-merge source
    // values over the leader's in-progress edits). Threaded through here rather
    // than via staleTime so we don't reuse possibly-stale cache.
    refetchOnMount?: boolean | 'always';
    refetchOnWindowFocus?: boolean;
  },
) {
  return useQuery({
    queryKey: ['event', eventId],
    queryFn: async () => {
      const response = await eventsApi.getEvent(eventId);
      // Merge route into event object (API returns { event, route } as siblings)
      // Issue #963: Route was being discarded, causing GPX import to appear broken
      return { ...response.event, route: response.route ?? null };
    },
    enabled: !!eventId,
    // #1244: Use 30s staleTime to prevent excessive refetching
    // Auth recovery handled by explicit refetch in EventDetailPage (#1239)
    staleTime: options?.staleTime ?? 1000 * 30, // 30 seconds
    ...(options?.refetchOnMount !== undefined && { refetchOnMount: options.refetchOnMount }),
    ...(options?.refetchOnWindowFocus !== undefined && { refetchOnWindowFocus: options.refetchOnWindowFocus }),
    retry: 1, // Retry once on error
  });
}

/**
 * Create a new event
 * 
 * On success: Invalidates ['events'] queries to refresh lists
 */
export function useCreateEvent(clubId: string) {
  const queryClient = useQueryClient();
  
  return useOroMutation<CreateEventResponse, CreateEventPayload>({
    mutationFn: async (payload: CreateEventPayload) => {
      // ═════ Layer 1: Frontend request deduplication ═════
      const deduplicator = RequestDeduplicator.getInstance();
      const idempotencyKey = generateIdempotencyKey(); // No userId – server-side scoped
      
      // Check if duplicate request already pending
      const pending = deduplicator.checkDuplicate(
        `POST /clubs/${clubId}/events`,
        payload
      );
      
      if (pending) {
        console.log('[useCreateEvent] Returning pending request for duplicate');
        return pending.promise as Promise<CreateEventResponse>;
      }
      
      // Create request with abort controller
      const controller = new AbortController();
      
      const promise = apiPost<CreateEventResponse>(
        `/clubs/${clubId}/events`,
        payload,
        {
          idempotencyKey,
          signal: controller.signal,
        }
      ).catch((e: Error) => {
        if (e.name === 'AbortError') {
          console.log('[useCreateEvent] Request aborted (duplicate detected)');
        }
        throw e;
      });
      
      // Register with deduplicator
      deduplicator.register(
        `POST /clubs/${clubId}/events`,
        payload,
        promise,
        controller
      );
      
      return promise;
      // ═════ End Layer 1 ═════
    },
    onSuccess: () => {
      // Refresh events list for this club
      queryClient.invalidateQueries({ 
        queryKey: ['events', { clubId }] 
      });
    },
    successToast: 'Event created',
    oro: {
      op: OroOperation.CreateEvent,
      retryPolicy: 'never',
      requiresIdempotency: true,
      debugLabel: 'Event creation form',
    },
  });
}

/**
 * Update event details
 * 
 * On success: Invalidates the club's event list and every cached event detail
 */
export function useUpdateEvent(eventId: string, clubId: string) {
  const queryClient = useQueryClient();

  return useOroMutation<{ event: Event }, UpdateEventPayload>({
    mutationFn: (payload: UpdateEventPayload) => eventsApi.updateEvent(eventId, payload),
    onSuccess: () => {
      // Refresh both the list and the specific event
      queryClient.invalidateQueries({ 
        queryKey: ['events', { clubId }] 
      });
      // Prefix match (#3844): eventId is the resolved UUID (Rule 50), but useEvent
      // caches under the route param, which may be the short_code.
      queryClient.invalidateQueries({ queryKey: ['event'] });
    },
    successToast: 'Event updated',
    oro: {
      op: OroOperation.UpdateEvent,
      retryPolicy: 'never',
      debugLabel: 'Event edit form',
    },
  });
}

/**
 * Delete an event
 *
 * On success: Invalidates event list queries
 *
 * @param clubId - Club ID for cache invalidation
 *
 * Usage:
 * - deleteEvent.mutate({ eventId: '...', deleteScope: 'this_only' })
 * - For recurring events, deleteScope can be 'this_only' or 'this_and_future' (#1203)
 */
interface DeleteEventParams {
  eventId: string;
  deleteScope?: DeleteScope;
}

export function useDeleteEvent(clubId: string) {
  const queryClient = useQueryClient();

  return useOroMutation<{ ok: true; deleted_count?: number }, DeleteEventParams>({
    mutationFn: ({ eventId, deleteScope }) => eventsApi.deleteEvent(eventId, deleteScope),
    onSuccess: () => {
      // Refresh manage events list (#1215)
      queryClient.invalidateQueries({
        queryKey: ['manage-events', clubId],
      });
      // Also refresh regular events list
      queryClient.invalidateQueries({
        queryKey: ['events', { clubId }],
      });
    },
    successToast: 'Event deleted',
    oro: {
      op: OroOperation.DeleteEvent,
      retryPolicy: 'never',
      debugLabel: 'Event deletion confirmation',
    },
  });
}

/**
 * RSVP to an event
 * 
 * On success: Invalidates both event list and specific event to refresh RSVP counts
 */
export function useEventRSVP(eventId: string, clubId: string) {
  const queryClient = useQueryClient();
  
  return useOroMutation<void, RSVPPayload>({
    mutationFn: (payload: RSVPPayload) => eventsApi.rsvpToEvent(eventId, payload),
    onSuccess: () => {
      // Refresh both the list (for RSVP counts) and the specific event
      queryClient.invalidateQueries({ 
        queryKey: ['events', { clubId }] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['event', eventId] 
      });
    },
    oro: {
      op: OroOperation.UpdateRSVP,
      retryPolicy: 'never',
      debugLabel: 'RSVP buttons',
    },
  });
}

/**
 * Upload event hero image
 *
 * On success: Invalidates event queries to refresh image data
 * @param eventId - UUID for API call
 * @param clubId - Club ID for list invalidation
 * @param queryKeyId - ID used in query key (may be short_code, defaults to eventId)
 */
export function useUploadEventHeroImage(eventId: string, clubId: string, queryKeyId?: string) {
  const queryClient = useQueryClient();
  const keyId = queryKeyId || eventId;

  return useOroMutation<UploadEventImageResponse, File>({
    mutationFn: (file: File) => eventsApi.uploadEventHeroImage(eventId, file),
    onSuccess: () => {
      // Refresh both the specific event and event lists to show new image
      // Use keyId which may be short_code from URL (not always UUID)
      queryClient.invalidateQueries({
        queryKey: ['event', keyId]
      });
      queryClient.invalidateQueries({
        queryKey: ['events', { clubId }]
      });
      queryClient.invalidateQueries({
        queryKey: ['events', 'my-clubs']
      });
    },
    oro: {
      op: OroOperation.UploadEventImage,
      retryPolicy: 'never',
      debugLabel: 'Event image upload',
    },
  });
}

/**
 * Remove event hero image
 *
 * On success: Invalidates event queries to refresh fallback image
 * @param eventId - UUID for API call
 * @param clubId - Club ID for list invalidation
 * @param queryKeyId - ID used in query key (may be short_code, defaults to eventId)
 */
export function useRemoveEventHeroImage(eventId: string, clubId: string, queryKeyId?: string) {
  const queryClient = useQueryClient();
  const keyId = queryKeyId || eventId;

  return useOroMutation<void, void>({
    mutationFn: () => eventsApi.removeEventHeroImage(eventId),
    onSuccess: () => {
      // Refresh both the specific event and event lists to show fallback image
      // Use keyId which may be short_code from URL (not always UUID)
      queryClient.invalidateQueries({
        queryKey: ['event', keyId]
      });
      queryClient.invalidateQueries({
        queryKey: ['events', { clubId }]
      });
      queryClient.invalidateQueries({
        queryKey: ['events', 'my-clubs']
      });
    },
    oro: {
      op: OroOperation.RemoveEventImage,
      retryPolicy: 'never',
      debugLabel: 'Event image removal',
    },
  });
}

/**
 * Fetch clubs where the user can create events (admin or owner)
 * 
 * Query Key: ['clubs-for-event-creation']
 * Returns: Array of clubs where user has admin or owner privileges
 */
export function useClubsForEventCreation() {
  return useQuery({
    queryKey: ['clubs-for-event-creation'],
    queryFn: eventsApi.getClubsForEventCreation,
    staleTime: 1000 * 60 * 5, // 5 minutes - club leadership doesn't change frequently
  });
}

// ============================================================================
// Pace Groups (#541 - v1.2)
// ============================================================================

/**
 * Fetch pace groups for an event
 *
 * Query Key: ['event', eventId, 'pace-groups']
 * Returns: Array of pace groups with participant counts
 */
export function usePaceGroups(eventId: string) {
  return useQuery({
    queryKey: ['event', eventId, 'pace-groups'],
    queryFn: async () => {
      const response = await eventsApi.getPaceGroups(eventId);
      return response.pace_groups;
    },
    enabled: !!eventId,
    staleTime: 1000 * 30, // 30 seconds - counts may change with RSVPs
  });
}

/**
 * Create a pace group for an event
 *
 * On success: Invalidates pace groups query
 */
export function useCreatePaceGroup(eventId: string) {
  const queryClient = useQueryClient();

  return useOroMutation<PaceGroupResponse, CreatePaceGroupPayload>({
    mutationFn: (payload: CreatePaceGroupPayload) =>
      eventsApi.createPaceGroup(eventId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['event', eventId, 'pace-groups'],
      });
    },
    successToast: 'Pace group created',
    oro: {
      op: OroOperation.CreatePaceGroup,
      retryPolicy: 'never',
      debugLabel: 'Pace group creation',
    },
  });
}

/**
 * Update a pace group
 *
 * On success: Invalidates pace groups query
 */
export function useUpdatePaceGroup(eventId: string) {
  const queryClient = useQueryClient();

  return useOroMutation<PaceGroupResponse, { paceGroupId: string; payload: UpdatePaceGroupPayload }>({
    mutationFn: ({ paceGroupId, payload }) =>
      eventsApi.updatePaceGroup(eventId, paceGroupId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['event', eventId, 'pace-groups'],
      });
    },
    successToast: 'Pace group updated',
    oro: {
      op: OroOperation.UpdatePaceGroup,
      retryPolicy: 'never',
      debugLabel: 'Pace group update',
    },
  });
}

/**
 * Delete a pace group
 *
 * On success: Invalidates pace groups query
 */
export function useDeletePaceGroup(eventId: string) {
  const queryClient = useQueryClient();

  return useOroMutation<{ ok: true }, string>({
    mutationFn: (paceGroupId: string) =>
      eventsApi.deletePaceGroup(eventId, paceGroupId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['event', eventId, 'pace-groups'],
      });
    },
    successToast: 'Pace group deleted',
    oro: {
      op: OroOperation.DeletePaceGroup,
      retryPolicy: 'never',
      debugLabel: 'Pace group deletion',
    },
  });
}

// Re-export types for convenience
export type { PaceGroup, CreatePaceGroupPayload, UpdatePaceGroupPayload };

// ============================================================================
// Event Series (#1208 - v1.2)
// ============================================================================

import type {
  ManageRow,
  SeriesDetailResponse,
  SeriesDeleteScope,
} from '../lib/api/events';

/**
 * Fetch unified manage rows (series + standalone events) for a club
 *
 * Query Key: ['manage-events', clubId, filter]
 * Returns: Array of ManageRow (series or event)
 *
 * #1693: server applies the upcoming/past split at request time using
 * `now - POST_EVENT_VISIBILITY_BUFFER_SECONDS` as the cutoff (see
 * backend/src/routes/events.ts:2658). Without a periodic refetch, an
 * event crossing the cutoff sits in the wrong tab until next mount /
 * focus / mutation invalidation. `refetchInterval: 60_000` matches the
 * `useNowSeconds` cadence so tab membership and the per-row badge drift
 * by at most 60s. `refetchIntervalInBackground` left off (default) so
 * hidden tabs don't burn requests; query-core pauses the interval when
 * `focusManager.isFocused()` is false (queryObserver.js:215).
 */
export function useManageEvents(clubId: string, filter?: 'upcoming' | 'past' | 'all') {
  return useQuery({
    queryKey: ['manage-events', clubId, filter],
    queryFn: async () => {
      const response = await eventsApi.getManageEvents(clubId, filter);
      return response.rows;
    },
    enabled: !!clubId,
    staleTime: 1000 * 60, // 1 minute
    refetchInterval: 60_000, // #1693: keep server-side tab filter accurate
  });
}

/**
 * Fetch series detail with instances
 *
 * Query Key: ['series', clubId, seriesId]
 * Returns: Series detail with upcoming and past instances
 */
export function useSeriesDetail(clubId: string, seriesId: string) {
  return useQuery({
    queryKey: ['series', clubId, seriesId],
    queryFn: () => eventsApi.getSeriesDetail(clubId, seriesId),
    enabled: !!clubId && !!seriesId,
    staleTime: 1000 * 60, // 1 minute
  });
}

/**
 * Delete series events
 *
 * On success: Invalidates manage-events queries
 */
interface DeleteSeriesParams {
  seriesId: string;
  scope?: SeriesDeleteScope;
}

export function useDeleteSeries(clubId: string) {
  const queryClient = useQueryClient();

  return useOroMutation<{ ok: true; deleted_count: number }, DeleteSeriesParams>({
    mutationFn: ({ seriesId, scope }) => eventsApi.deleteSeries(clubId, seriesId, scope),
    onSuccess: (_data, variables) => {
      // Refresh manage events list
      queryClient.invalidateQueries({
        queryKey: ['manage-events', clubId],
      });
      // Also invalidate regular events list
      queryClient.invalidateQueries({
        queryKey: ['events', { clubId }],
      });
      // Invalidate the series-detail cache (#2189). For scope=next the series
      // and its later occurrences survive, so reopening the same series within
      // staleTime would otherwise serve the just-deleted occurrence + stale
      // counts. Mirrors the mobile useDeleteSeries fix (#2128/#2188).
      queryClient.invalidateQueries({
        queryKey: ['series', clubId, variables.seriesId],
      });
    },
    successToast: 'Events deleted',
    oro: {
      op: OroOperation.DeleteEvent, // Reuse existing operation
      retryPolicy: 'never',
      debugLabel: 'Series deletion',
    },
  });
}

/**
 * Update a series (#2198)
 *
 * There is no series-update endpoint. The series edit page PATCHes the FIRST
 * upcoming instance with `edit_scope: 'this_and_future'` — the backend fans
 * the update out to every instance with `starts_at >=` the anchor's and syncs
 * the event_series metadata row. One request = one error surface (no silent
 * partial failure), and past instances are never touched (upcoming-only scope, not Rule 14 — #2200).
 *
 * On success: invalidates manage-events, events list, series detail, and
 * event detail queries (every instance may be cached from a detail visit).
 */
interface UpdateSeriesParams {
  seriesId: string;
  /** id of the FIRST upcoming instance — the edit_scope fan-out anchor */
  anchorEventId: string;
  payload: UpdateEventPayload;
}

export function useUpdateSeries(clubId: string) {
  const queryClient = useQueryClient();

  return useOroMutation<{ event: Event }, UpdateSeriesParams>({
    mutationFn: ({ anchorEventId, payload }) =>
      eventsApi.updateEvent(anchorEventId, { ...payload, edit_scope: 'this_and_future' }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['manage-events', clubId] });
      queryClient.invalidateQueries({ queryKey: ['events', { clubId }] });
      queryClient.invalidateQueries({ queryKey: ['series', clubId, variables.seriesId] });
      // Prefix match: ['event', <instanceId>] for every cached instance
      queryClient.invalidateQueries({ queryKey: ['event'] });
    },
    successToast: 'Series updated',
    oro: {
      op: OroOperation.UpdateEvent,
      retryPolicy: 'never',
      debugLabel: 'Series edit Save',
    },
  });
}

// Re-export series types
export type { ManageRow, SeriesDetailResponse, SeriesDeleteScope };
